import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { roomsAPI, bookingsAPI } from "../services/api";
import { formatDateStr } from "../components/calendar/calendarUtils";
import RoomOverviewTable from "../components/calendar/RoomOverviewTable";
import WeekCalendarView from "../components/calendar/WeekCalendarView";
import { Room } from "../types/room";
import { Booking } from "../types/booking";

function CalendarPage(): React.ReactElement {
  const { user } = useAuth();
  const canManageBookings = user?.role === "admin" || user?.role === "superadmin";

  const [rooms, setRooms] = useState<Room[]>([]);
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);

  // ===================== DATA FETCHING =====================

  const fetchRooms = useCallback(async () => {
    try {
      const roomsRes = await roomsAPI.getAll();
      let allRooms: Room[] = roomsRes.data.data || [];
      if (user?.role === "roommaster" && user?.managedRooms && Array.isArray(user.managedRooms)) {
        const managedIds = user.managedRooms.map((r) => (typeof r === "object" && r._id ? r._id : r as string));
        allRooms = allRooms.filter((room) => managedIds.includes(room._id));
      }
      setRooms(allRooms);
    } catch (error) {
      console.error("Error fetching rooms:", error);
    }
  }, [user]);

  const fetchAllBookings = useCallback(async () => {
    try {
      const from = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
      const to = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
      const res = await bookingsAPI.filter({
        fromDate: formatDateStr(from),
        toDate: formatDateStr(to),
      });
      let bookings: Booking[] = res.data.data || [];
      if (user?.role === "roommaster" && user?.managedRooms && Array.isArray(user.managedRooms)) {
        const managedIds = user.managedRooms.map((r) => (typeof r === "object" && r._id ? r._id : r as string));
        bookings = bookings.filter((b) => {
          const bRoomId = typeof b.roomId === "object" ? b.roomId._id : b.roomId;
          return managedIds.includes(bRoomId);
        });
      }
      setAllBookings(bookings);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  }, [user]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchRooms();
      await fetchAllBookings();
      setLoading(false);
    };
    init();
  }, [fetchRooms, fetchAllBookings]);

  // ===================== HANDLERS =====================

  const handleSelectRoom = (room: Room): void => {
    setSelectedRoom(room);
  };

  const handleBackToRooms = (): void => {
    setSelectedRoom(null);
  };

  const handleBookingUpdated = async (): Promise<void> => {
    await fetchAllBookings();
  };

  // ===================== RENDER =====================

  if (loading && rooms.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 text-lg">Đang tải dữ liệu...</div>
      </div>
    );
  }

  // View 1: Room overview table
  if (!selectedRoom) {
    return (
      <RoomOverviewTable
        rooms={rooms}
        allBookings={allBookings}
        onSelectRoom={handleSelectRoom}
        currentUser={user}
        onBookingUpdated={handleBookingUpdated}
      />
    );
  }

  // View 2: FullCalendar week view
  return (
    <WeekCalendarView
      selectedRoom={selectedRoom}
      allBookings={allBookings}
      canManageBookings={canManageBookings}
      onBackToRooms={handleBackToRooms}
      onBookingUpdated={handleBookingUpdated}
      rooms={rooms}
    />
  );
}

export default CalendarPage;
